import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import {
  LANGUAGE_STORAGE_KEY,
  LANGUAGE_USER_SET_KEY,
  resolveInitialAppLanguage,
  setAppLanguage,
  type AppLanguage,
} from "@/lib/appLanguage";
import { interpolate } from "@/lib/interpolate";
import {
  ensureLocaleLoaded,
  getLocaleDictionaries,
  type LocaleDictionaries,
} from "@/translations/localeDictionaries";

// Load Chinese font dynamically
const loadChineseFont = () => {
  if (document.getElementById("chinese-font-link")) return;

  const link = document.createElement("link");
  link.id = "chinese-font-link";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;700&display=swap";
  document.head.appendChild(link);
};

export type Language = AppLanguage;
type PluralCategory = ReturnType<Intl.PluralRules["select"]>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  tPlural: (baseKey: string, count: number, values?: Record<string, string | number>) => string;
  detectLanguageFromLocation?: (lat: number, lon: number) => void;
  /** True while a lazy locale chunk is loading (switch or initial non-no). */
  isLocaleLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: "no" as Language,
      setLanguage: () => {},
      t: (key: string) => key,
      tPlural: (baseKey: string, count: number, values?: Record<string, string | number>) =>
        tPlural("no", baseKey, count, values),
      isLocaleLoading: false,
    };
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

const VALID_LANGUAGES: ReadonlySet<string> = new Set(["no", "en", "uk", "ar", "zh"]);

const pluralRuleLocales: Record<Language, string> = {
  no: "nb",
  en: "en",
  uk: "uk",
  ar: "ar",
  zh: "zh",
};

const pluralRules = new Map<Language, Intl.PluralRules>();

export const getPluralCategory = (language: Language, count: number): PluralCategory => {
  const normalizedLanguage = VALID_LANGUAGES.has(language) ? language : "no";
  let rules = pluralRules.get(normalizedLanguage);
  if (!rules) {
    rules = new Intl.PluralRules(pluralRuleLocales[normalizedLanguage]);
    pluralRules.set(normalizedLanguage, rules);
  }
  return rules.select(count);
};

/** Readable string from a dictionary entry; rejects non-strings and empty values. */
const pickTranslation = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : undefined;
};

/**
 * requested language → Norwegian → key (last resort).
 * Never throws; never returns undefined.
 * Unloaded lazy locales are empty objects → Norwegian fallback (no raw-key FOUC).
 */
export const resolveTranslation = (
  language: Language,
  key: string,
  dicts: LocaleDictionaries = getLocaleDictionaries(),
): string => {
  const dict = dicts[language] ?? dicts.no;
  return pickTranslation(dict[key]) ?? pickTranslation(dicts.no[key]) ?? key;
};

const resolveLocaleOnlyTranslation = (
  language: Language,
  key: string,
  dicts: LocaleDictionaries,
): string => {
  const normalizedLanguage = VALID_LANGUAGES.has(language) ? language : "no";
  const localeDict = dicts[normalizedLanguage] ?? dicts.no;
  const localeOnlyDicts = {
    no: normalizedLanguage === "no" ? localeDict : {},
    en: {},
    uk: {},
    ar: {},
    zh: {},
    [normalizedLanguage]: localeDict,
  } as LocaleDictionaries;
  return resolveTranslation(normalizedLanguage, key, localeOnlyDicts);
};

export const tPlural = (
  language: Language,
  key: string,
  count: number,
  values: Record<string, string | number> = {},
  dicts: LocaleDictionaries = getLocaleDictionaries(),
): string => {
  const category = getPluralCategory(language, count);
  const categoryKey = `${key}_${category}`;
  const categoryTemplate = resolveLocaleOnlyTranslation(language, categoryKey, dicts);
  const template =
    categoryTemplate !== categoryKey
      ? categoryTemplate
      : resolveTranslation(language, `${key}_other`, dicts);

  return interpolate(template, { ...values, n: count, count });
};

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const initial = resolveInitialAppLanguage();
    setAppLanguage(initial);
    return initial;
  });

  const [dictsVersion, setDictsVersion] = useState(0);
  const [isLocaleLoading, setIsLocaleLoading] = useState(
    () => resolveInitialAppLanguage() !== "no",
  );
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  const bumpDicts = useCallback(() => {
    setDictsVersion((v) => v + 1);
  }, []);

  const setLanguage = useCallback(
    (lang: Language) => {
      setLanguageState(lang);
      setAppLanguage(lang);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        window.localStorage.setItem(LANGUAGE_USER_SET_KEY, "1");
      }
      if (lang === "no") {
        setIsLocaleLoading(false);
        return;
      }
      setIsLocaleLoading(true);
      void ensureLocaleLoaded(lang).then(() => {
        bumpDicts();
        setIsLocaleLoading(false);
      });
    },
    [bumpDicts],
  );

  // Eager Norwegian is always present; load active non-no locale (incl. ?lang=).
  useEffect(() => {
    let cancelled = false;
    if (language === "no") {
      setIsLocaleLoading(false);
      return;
    }
    setIsLocaleLoading(true);
    void ensureLocaleLoaded(language).then(() => {
      if (cancelled) return;
      bumpDicts();
      setIsLocaleLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [language, bumpDicts]);

  useEffect(() => {
    setAppLanguage(language);
  }, [language]);

  useEffect(() => {
    if (language === "zh") {
      loadChineseFont();
    }
  }, [language]);

  const detectLanguageFromLocation = (_lat: number, _lon: number) => {
    if (isAutoDetected) return;
    setIsAutoDetected(true);
  };

  // dictsVersion forces re-render when a lazy chunk arrives so t() picks new strings.
  void dictsVersion;
  const dicts = getLocaleDictionaries();

  const t = (key: string): string => resolveTranslation(language, key, dicts);
  const tPluralForCurrentLanguage = (
    baseKey: string,
    count: number,
    values?: Record<string, string | number>,
  ): string => tPlural(language, baseKey, count, values, dicts);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        tPlural: tPluralForCurrentLanguage,
        detectLanguageFromLocation,
        isLocaleLoading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
